// Utility functions shared across the application

import moment from 'moment';

/* ARM Mali drivers (seen via ANGLE on Android Chrome) corrupt deck.gl's GPU
attribute-transition ping-pong buffers; Adreno and Xclipse are unaffected.
See requirements/weather-pin-flicker-debug.md.

working
ANGLE (Samsung Electronics Co. Ltd., ANGLE ((Samsung Xclipse 960) on Vulkan 1.4.304), OpenGL ES 3.2)
ANGLE(Qualcomm, Adreno 9TM) 610, OpenGL ES 3.2)

not working
ANGLE (ARM, Mali-G57 MC3, OpenGL ES 3.2)
ANGLE (ARM, Mali-G615 MC6, OpenGL ES 3.2)
ANGLE (ARM, Mali-G72 MP3, OpenGL ES 3.2)
*/
const BROKEN_GPU_TRANSITION_PATTERNS = [/mali/i];

export function getGpuRendererString() {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
  const dbg = gl?.getExtension('WEBGL_debug_renderer_info');
  return dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : null;
}

export function supportsGpuAttributeTransitions(rendererString = getGpuRendererString()) {
  if (!rendererString) return true;
  return !BROKEN_GPU_TRANSITION_PATTERNS.some(pattern => pattern.test(rendererString));
}

export function isValidNumber(val) {
  return typeof val !== 'undefined' && val != null && !isNaN(parseFloat(val)) && isFinite(val);
}

export function shortTimeFormat(seconds) {
    const absTime = Math.abs(seconds);
    const hours = Math.floor(absTime / 3600);
    const mins = Math.floor((absTime % 3600) / 60);
    const secs = absTime % 60;

    let result = seconds < 0 ? "-" : "";
    if (hours > 0) {
        result += `${hours}h${mins}m`;
    } else if (mins > 0) {
        result += `${mins}m${secs}s`;
    } else {
        result += `${secs}s`;
    }
    return result;
}

export function stringifyWithMaxDepth(obj, maxDepth = 2, space = 2) {

  function replacer(key, value) {
    const depth = this.__depth__ || 0

    if (typeof value === 'object' && value !== null) {
      if (!value.__depth__) {
        // Attach depth to nested objects
        Object.defineProperty(value, '__depth__', {
          value: depth + 1,
          enumerable: false,
        })
      }
      if (depth >= maxDepth) {
        return { __inline__: JSON.stringify(value) }
      }
    }

    return value
  }

  // First stringify using our depth-aware replacer
  const raw = JSON.stringify(obj, replacer, space)

  // Then clean up: replace { "__inline__": "..." } with the raw JSON
  const result = raw.replace(
    /{\s*"__inline__"\s*:\s*"((?:[^"\\]|\\.)*)"\s*}/g,
    (_, inner) => {
      const unescaped = inner
        .replace(/\\"/g, '"') // remove escaped quotes
        .replace(/\\\\/g, '\\') // remove double backslashes
      return unescaped
    }
  )

  return result
}


export function formatDate(date, format = 'YYYY-MM-DD HH:mm:ss') {
  return moment(date).format(format);
}


export function toISO8601(date) {
  return moment(date).toISOString();
}

let _toastTimeoutId = null;

export function showToast(message) {
  let toast = document.getElementById('sharedToast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'sharedToast';
    toast.className = 'toast';
    document.body.appendChild(toast);
  }
  if (_toastTimeoutId) {
    clearTimeout(_toastTimeoutId);
    _toastTimeoutId = null;
  }
  toast.textContent = message;
  toast.style.display = 'block';
  _toastTimeoutId = setTimeout(() => {
    toast.style.display = 'none';
    _toastTimeoutId = null;
  }, 3000);
}

export function notifyError(message, e) {
  console.log(message,e);
  showToast(message,e?.message);
}

function bearingDelta(from, to, accessFn = (e) => e) {
  return (accessFn(to) - accessFn(from) + 540) % 360 - 180;
}

export function averageAngle(angles, accessFn = (e) => e) {
  if (angles == null || angles.length === 0) return NaN;
  if (angles.length === 1) return angles[0];
  angles = angles.filter(a => Number.isFinite(accessFn(a)));
  if (angles.length === 0) return NaN;
  const from = angles[0];
  const avgDelta = angles.map(to => bearingDelta(from, to, accessFn)).reduce((acc, b) => acc + b) / angles.length;
  return parseInt(((accessFn(from) + avgDelta) % 360 + 360) % 360);
}

// returns a [0,1] multiplier: full rate above rampEnd, eased to zero at rampStart
// cubic smoothstep
export function cubicSmooth(v, rampStart = 5, rampEnd = 25) {
  const val = Math.abs(v);
  if (val >= rampEnd) return 1;
  if (val <= rampStart) return 0;
  const t = (val - rampStart) / (rampEnd - rampStart);
  return Math.pow(t,3) * (3 - 2 * t);
}

// circular mean of an array of bearings in degrees
export function circularMeanDeg(angles) {
  let sinSum = 0, cosSum = 0;
  for (const a of angles) {
    const r = a * Math.PI / 180;
    sinSum += Math.sin(r);
    cosSum += Math.cos(r);
  }
  return (Math.atan2(sinSum, cosSum) * 180 / Math.PI + 360) % 360;
}

export function initialiseTimer(focusEl, targetEl, time, timer) {
    focusEl.addEventListener('mouseenter', () => {
        targetEl.classList.add('visible');
        cancelHide(timer)
    });
    focusEl.addEventListener('mouseleave', () => { scheduleHide(targetEl, time, timer) });
    focusEl.addEventListener('focus', () => { cancelHide(timer) });
    focusEl.addEventListener('blur', (e) => {
        if (!targetEl.contains(e.relatedTarget)) {
            targetEl.classList.add('visible');
            scheduleHide(targetEl, time, timer)
        }
    });
}

function scheduleHide(el, time, timer) {
    timer.id = setTimeout(() => {
        el.classList.remove('visible');
    }, time);
}

function cancelHide(timer) {
    if (timer.id != null) {
        clearTimeout(timer.id);
        timer.id = null;
    }
}